import { ReactElement } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'
import CheckIcon from '@mui/icons-material/Check'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Box, ListItemIcon, ListItemText, ListSubheader, MenuItem } from '@mui/material'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { NestedMenuItem } from '@ui/navigation/NestedMenu'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import { useIsAnyActionBlocked } from '@editorHooks'
import { organisationLabel } from '@utils'
import { compactMenuProps } from '../../compactMenuProps'
import useActiveOrganisation from '../../hooks/useActiveOrganisation'

interface Props {
  parentMenuOpen: boolean
  onSwitched: () => void
}

/**
 * Lets a user who belongs to several organisations choose which one they are
 * working in. Hidden for everyone else — which is nearly everyone, and a
 * single-entry picker is just a puzzle.
 *
 * The menu entry states the current organisation and opens the alternatives in
 * a submenu, so the account menu answers "which organisation am I in?" without
 * being a list of every one the user belongs to.
 */
const OrganisationSwitcher = ({ parentMenuOpen, onSwitched }: Props): ReactElement | null => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const setActiveOrganisationGuid = useAppStore(state => state.setActiveOrganisationGuid)
  const { organisations, activeOrganisation: active } = useActiveOrganisation()
  const navigate = useNavigate()
  const isInProject = Boolean(useMatch('/editor/:projectId/*'))
  const { isBlockedFn: isAnyActionBlockedFn } = useIsAnyActionBlocked()

  if (!organisations || organisations.length < 2 || !active) return null

  const handleSwitch = async (organisation: MyOrganisationDTO) => {
    if (organisation.guid === active.guid) {
      onSwitched()
      return
    }

    if (isInProject) {
      // The open project belongs to the organisation being left, so the editor would switch
      // straight back to it. Leave the project for the project list of the new organisation.
      if (isAnyActionBlockedFn()) {
        enqueueSnackbar(
          'Bitte speichern Sie Ihre Änderungen, bevor Sie die Organisation wechseln.',
          { variant: 'warning', preventDuplicate: true },
        )
        return
      }
      navigate('/')
    }

    setActiveOrganisationGuid(organisation.guid)
    // Everything fetched so far belongs to the organisation just left —
    // projects, library, templates. Invalidating everything is cheaper to
    // reason about than deciding per query: mounted queries refetch right
    // away, the rest refetch the next time they are used.
    queryClient.invalidateQueries()
    enqueueSnackbar(`Organisation gewechselt: ${organisationLabel(organisation)}`, {
      variant: 'success',
    })
    onSwitched()
  }

  return (
    <Box pb={1}>
      <ListSubheader sx={{ lineHeight: 2 }}>Organisation</ListSubheader>

      <NestedMenuItem
        label={organisationLabel(active)}
        parentMenuOpen={parentMenuOpen}
        direction="left"
        leftIcon={<ChevronLeftIcon />}
        MenuProps={compactMenuProps}
        data-cy="menu-organisation-switcher"
      >
        {organisations.map(organisation => (
          <MenuItem
            key={organisation.guid}
            onClick={() => handleSwitch(organisation)}
            selected={organisation.guid === active.guid}
            data-cy={`btn-switch-organisation-${organisation.guid}`}
          >
            <ListItemIcon>
              {organisation.guid === active.guid && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{organisationLabel(organisation)}</ListItemText>
          </MenuItem>
        ))}
      </NestedMenuItem>
    </Box>
  )
}

export default OrganisationSwitcher
